import { useEffect, useCallback } from 'react'
import { markBookedEventLog } from 'src/consumer/services/featureServices'
import { sleep } from 'src/mentor/helpers/utilityFunctions'

/**
 * Custom hook for logging session status with device (camera/microphone) states
 *
 * This hook periodically logs 'REFRESH' events to track:
 * - Session activity (heartbeat every 3 minutes)
 * - Device state changes (camera/microphone toggles)
 *
 * Useful for:
 * - Analytics & session quality monitoring
 * - Debugging connection/device issues
 * - Compliance & audit logs
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.sessionStarted - Whether the session has started
 * @param {boolean} params.isCameraOpen - Current camera state
 * @param {boolean} params.isAudioOpen - Current microphone state
 * @param {string} params.bookedEventId - The booked event/session ID
 * @param {string} params.userRole - User's role (mentor/panelist/user)
 *
 * @example
 * useSessionStatusLogging({
 *   sessionStarted: true,
 *   isCameraOpen: true,
 *   isAudioOpen: false,
 *   bookedEventId: 'event-123',
 *   userRole: 'mentor'
 * })
 */
export const useSessionStatusLogging = ({
  sessionStarted,
  isCameraOpen,
  isAudioOpen,
  bookedEventId,
  userRole,
  rtcProvider = null,
}) => {
  const handleRefreshStatusForUser = useCallback(async () => {
    if (!bookedEventId || !userRole) {
      return
    }

    // Small delay before logging (matches original behavior)
    await sleep(800)

    // Log REFRESH event with current device states
    markBookedEventLog(bookedEventId, 'REFRESH', userRole, {
      camera: isCameraOpen,
      mic: isAudioOpen,
    }, rtcProvider)
  }, [bookedEventId, userRole, isCameraOpen, isAudioOpen, rtcProvider])

  useEffect(() => {
    let interval = null

    if (sessionStarted && bookedEventId && userRole) {
      // Immediately log status when session starts
      handleRefreshStatusForUser()

      // Set up periodic logging every 3 minutes (180000ms)
      interval = setInterval(() => {
        handleRefreshStatusForUser()
      }, 180000)
    } else {
      // Clear interval if session ends
      if (interval) {
        clearInterval(interval)
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [sessionStarted, isCameraOpen, isAudioOpen, handleRefreshStatusForUser])

  // No return value needed - this hook only manages side effects
}
